import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { Reveal, Eyebrow, PageHero, PrimaryLink, OutlineLink } from "@/components/shared";
import { programs, servicePages, IMAGES } from "@/data/content";
import { useLang } from "@/i18n";

const ProgramDetail = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const program = programs.find((p) => p.id === slug);
  const service = servicePages[slug];

  if (!program && !service) {
    return (
      <section className="bg-ivory" data-testid="program-not-found">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-28 text-center">
          <h1 className="font-serif text-4xl font-semibold text-charcoal">{t("detail.notFound")}</h1>
          <p className="mt-4 text-bodytext/75">{t("detail.notFoundBody")}</p>
          <div className="mt-8">
            <PrimaryLink to="/our-work" testId="not-found-back-button">{t("detail.explore")}</PrimaryLink>
          </div>
        </div>
      </section>
    );
  }

  const kn = lang === "kn" && program?.kn ? program.kn : null;
  const title = program ? (kn ? `${program.knName} (${program.name})` : program.name) : service.title;
  const tagline = program ? (kn ? kn.tagline : program.tagline) : service.tagline;
  const body = program ? (kn ? kn.long : program.long) : service.body;
  const facts = program ? (kn ? kn.facts : program.facts) : null;
  const facilities = program ? (kn ? kn.facilities : program.facilities) : null;
  const Icon = program ? program.icon : service.icon;
  const image = IMAGES[(program || service).image];
  const extraLink = service?.link;

  return (
    <>
      <PageHero
        testId={`program-hero-${slug}`}
        eyebrow={t("ow.heroEyebrow")}
        title={`${title} — ${tagline}`}
        intro={program ? (kn ? kn.desc : program.desc) : undefined}
      />

      <section className="bg-ivory" data-testid={`program-detail-${slug}`}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <Link to="/our-work" className="link-arrow text-[14px]" data-testid="program-back-link">
            <ArrowLeft size={16} aria-hidden="true" /> {t("detail.back")}
          </Link>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-stretch">
            <Reveal>
              {program ? (
                <div className="w-24 h-24 rounded-2xl flex items-center justify-center p-2.5 border border-[rgba(41,39,34,0.08)]" style={{ backgroundColor: program.emblemBg }} data-testid={`program-emblem-${slug}`}>
                  <img src={program.emblem} alt={`${program.name} emblem`} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <Icon size={34} strokeWidth={1.5} className="text-saffron" aria-hidden="true" />
              )}
              <h2 className="mt-4 text-[30px] sm:text-4xl font-semibold text-charcoal">{tagline}</h2>
              {body.map((para, i) => (
                <p key={i} className="mt-4 text-bodytext/80 leading-relaxed">
                  {para}
                </p>
              ))}
              {program?.pledge && !kn && (
                <>
                  <p className="mt-4 text-bodytext/80 leading-relaxed">{program.pledgeIntro}</p>
                  <ul className="mt-3 space-y-2.5" data-testid={`program-pledge-${slug}`}>
                    {program.pledge.map((item) => (
                      <li key={item} className="flex gap-3 items-start">
                        <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-saffron shrink-0" aria-hidden="true" />
                        <span className="text-bodytext/80 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-bodytext/80 leading-relaxed">{program.pledgeClosing}</p>
                </>
              )}
              <div className="mt-8 flex flex-wrap gap-4">
                <PrimaryLink to="/donate" testId={`program-donate-${slug}`}>{t("detail.support")}</PrimaryLink>
                {extraLink ? (
                  <OutlineLink to={extraLink.to} testId={`program-extra-link-${slug}`}>{extraLink.label}</OutlineLink>
                ) : (
                  <OutlineLink to="/volunteer" testId={`program-volunteer-${slug}`}>{t("detail.volunteer")}</OutlineLink>
                )}
              </div>
            </Reveal>
            <Reveal delay={100} className="h-full">
              <div className="img-zoom !rounded-2xl overflow-hidden aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[380px]">
                <img src={program?.scene || image} alt={`${title} — ${tagline}`} className="w-full h-full object-cover object-center" data-testid={`program-image-${slug}`} />
              </div>
            </Reveal>
          </div>

          {program && (
            <Reveal className="mt-16">
              <Link
                to="/maha-karuna"
                className="inline-block font-sans text-[13px] font-extrabold uppercase tracking-[0.2em] text-saffrondeep no-underline hover:text-saffron transition-colors duration-200"
                data-testid={`program-karuna-note-${slug}`}
              >
                {t("detail.karunaNote")}
              </Link>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid={`program-facts-${slug}`}>
                {facts.map(([k, v]) => (
                  <div key={k} className="card-ivory p-6">
                    <p className="font-sans text-[12px] font-extrabold uppercase tracking-[0.18em] text-saffron">{k}</p>
                    <p className="mt-2 font-serif text-[22px] font-semibold text-charcoal">{v}</p>
                  </div>
                ))}
              </div>
              <h3 className="mt-12 font-serif text-[26px] font-semibold text-charcoal">{t("detail.facilities")}</h3>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`program-facilities-${slug}`}>
                {facilities.map((f) => (
                  <li key={f} className="flex items-center gap-3 border border-[rgba(41,39,34,0.12)] rounded-xl px-4 py-3 bg-cream">
                    <Check size={17} strokeWidth={2} className="text-saffron shrink-0" aria-hidden="true" />
                    <span className="font-sans font-semibold text-[14.5px] text-charcoal">{f}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
};

export default ProgramDetail;
